/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, CreditCard, CheckCircle2, Clock, Loader2, TrendingUp, Wallet, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { meApi, formatApiError } from "@/lib/api";
import { formatDateLong } from "@/lib/date";

function fmtEur(v) {
  return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(iso) {
  return iso ? (formatDateLong(iso) || "—") : "—";
}

/**
 * Espace Client — Échéances de paiement & décompte J-XX.
 * Aggregates the client's signed devis, deposits paid, remaining balance, and days-to-event.
 */
export default function Echeances() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    meApi.echeances()
      .then(setData)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <p className="text-white/55 italic flex items-center gap-2">
      <Loader2 size={14} className="animate-spin" /> Chargement…
    </p>
  );

  if (!data?.has_devis) return (
    <div data-testid="ec-echeances">
      <header className="mb-8">
        <p className="eyebrow">Espace client</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          Mes <span className="italic gold-text">échéances.</span>
        </h1>
      </header>
      <div className="card-luxe p-12 text-center">
        <Wallet size={40} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
        <p className="text-white/70 mb-2">Aucun devis en cours.</p>
        <p className="text-white/45 text-sm mb-6">Dès qu'un devis vous sera adressé, vous retrouverez ici votre échéancier de paiement.</p>
        <Link to="/contact" className="btn-ghost-gold inline-flex">Demander un devis</Link>
      </div>
    </div>
  );

  const pctPaid = data.total_ttc > 0 ? Math.min(100, Math.round((data.total_paid / data.total_ttc) * 100)) : 0;
  const days = data.days_to_event;

  return (
    <div data-testid="ec-echeances">
      <header className="mb-8">
        <p className="eyebrow">Espace client</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          Mes <span className="italic gold-text">échéances.</span>
        </h1>
        <p className="text-white/60 text-sm md:text-base mt-3 max-w-2xl">
          Retrouvez ici le décompte de votre événement, les montants réglés et le reste à payer.
        </p>
      </header>

      {/* Countdown */}
      <div className="card-luxe p-8 md:p-10 mb-8 relative overflow-hidden" data-testid="echeances-countdown">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#DCB457]/6 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-8">
          <div className="flex-1 min-w-[200px]">
            <p className="eyebrow">
              {!data.event_date ? "Date à confirmer" : data.event_passed ? "Événement passé" : "Décompte"}
            </p>
            <p className="font-serif-luxe text-6xl md:text-7xl mt-2 gold-text leading-none" data-testid="echeances-days">
              {!data.event_date ? "—" : data.event_passed ? "—" : (days != null && days >= 0 ? `J-${days}` : "—")}
            </p>
            <p className="text-white/60 mt-3 flex items-center gap-2 text-sm">
              <Calendar size={14} strokeWidth={1.4} className="text-[#DCB457]" />
              {data.event_type || "Événement"} {data.event_date ? `— ${fmtDate(data.event_date)}` : "— date en cours de définition"}
            </p>
          </div>
          <div className="min-w-[250px]">
            <p className="eyebrow mb-2">Progression paiement</p>
            <div className="h-3 bg-black/40 border border-[#DCB457]/20 relative overflow-hidden">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${pctPaid}%`,
                  background: "linear-gradient(90deg, #DCB457, #F0D888)",
                }}
                data-testid="echeances-progress-bar"
              />
            </div>
            <p className="text-xs text-white/55 mt-2 flex items-center justify-between">
              <span>{fmtEur(data.total_paid)} réglés</span>
              <span className="gold-text font-bold">{pctPaid}%</span>
            </p>
          </div>
        </div>
      </div>

      {/* Financial breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <SummaryCard
          testid="echeances-total"
          icon={TrendingUp}
          label="Total prestation"
          value={fmtEur(data.total_ttc)}
          hint={`Devis ${data.devis_number || ""}`}
        />
        <SummaryCard
          testid="echeances-paid"
          icon={CheckCircle2}
          label="Déjà réglé"
          value={fmtEur(data.total_paid)}
          hint={`${pctPaid}% du montant`}
          color="emerald"
        />
        <SummaryCard
          testid="echeances-remaining"
          icon={Wallet}
          label="Reste à payer"
          value={fmtEur(data.remaining)}
          hint={data.remaining > 0 ? "À régler avant l'événement" : "Payé intégralement 🎉"}
          color={data.remaining > 0 ? "gold" : "emerald"}
        />
      </div>

      {/* Milestones timeline */}
      <div className="card-luxe p-6 md:p-8" data-testid="echeances-milestones">
        <div className="flex items-center gap-2 mb-5">
          <CreditCard size={16} strokeWidth={1.4} className="text-[#DCB457]" />
          <h2 className="font-serif-luxe text-xl md:text-2xl">Échéancier</h2>
        </div>
        <ul className="space-y-4">
          {data.milestones.map((m, i) => (
            <li key={i} className="flex items-start gap-4" data-testid={`echeance-item-${i}`}>
              <div className={`w-10 h-10 flex items-center justify-center border rounded-sm shrink-0 ${
                m.done ? "bg-emerald-500/15 border-emerald-400/40" : "bg-black/40 border-[#DCB457]/30"
              }`}>
                {m.done
                  ? <CheckCircle2 size={16} strokeWidth={1.4} className="text-emerald-300" />
                  : <Clock size={16} strokeWidth={1.4} className="text-[#DCB457]" />
                }
              </div>
              <div className="flex-1 min-w-0 flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className={`font-serif-luxe text-lg ${m.done ? "" : "text-white/85"}`}>{m.label}</p>
                  {m.date && <p className="text-xs text-white/50">{fmtDate(m.date)}</p>}
                </div>
                {m.amount != null && (
                  <p className={`font-serif-luxe text-lg ${m.done ? "text-emerald-300" : "gold-text"}`}>
                    {fmtEur(m.amount)}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>

        {data.remaining > 0 && data.deposit_paid <= 0 && (
          <div className="mt-8 pt-6 border-t border-[#DCB457]/15">
            <p className="text-sm text-white/70 mb-4">
              Vous pouvez régler votre acompte en ligne par carte bancaire depuis vos documents.
            </p>
            <Link
              to="/espace-client/documents"
              data-testid="echeances-pay-cta"
              className="btn-gold inline-flex items-center gap-2"
            >
              <CreditCard size={14} strokeWidth={1.5} /> Payer mon acompte
            </Link>
          </div>
        )}
      </div>

      {/* Solde à régler — carte détaillée */}
      {data.remaining > 0 && data.deposit_paid > 0 && !data.event_passed && (
        <SoldeCard data={data} />
      )}
    </div>
  );
}

function copyToClipboard(text, label) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copié.`),
    () => toast.error("Copie impossible."),
  );
}

function SoldeCard({ data }) {
  const days = data.days_to_solde_due;
  const overdue = days !== null && days < 0;
  const urgent = !overdue && days !== null && days <= 7;
  const pi = data.payment_info || {};
  const solde = data.solde_amount || data.remaining;

  return (
    <div
      className={`mt-8 card-luxe p-6 md:p-8 border-l-4 ${
        overdue ? "border-l-red-400" : urgent ? "border-l-amber-400" : "border-l-[#DCB457]"
      }`}
      data-testid="echeances-solde-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <p className="eyebrow flex items-center gap-2">
            {overdue || urgent ? <AlertTriangle size={12} className={overdue ? "text-red-300" : "text-amber-300"} /> : <Wallet size={12} className="text-[#DCB457]" />}
            Solde à régler
          </p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-2">
            {fmtEur(solde)}
          </h2>
          <p className="text-white/60 text-sm mt-2">
            À régler <strong className="text-white/85">avant le mercredi précédant votre événement</strong>
          </p>
        </div>
        <div className={`text-right shrink-0 px-5 py-4 border ${overdue ? "border-red-400/40 bg-red-500/5" : urgent ? "border-amber-400/40 bg-amber-500/5" : "border-[#DCB457]/25 bg-[#DCB457]/5"}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/55">Date d'échéance</p>
          <p className={`font-serif-luxe text-xl md:text-2xl mt-1 ${overdue ? "text-red-300" : urgent ? "text-amber-300" : "gold-text"}`} data-testid="solde-due-date">
            {fmtDate(data.solde_due_date)}
          </p>
          {days !== null && (
            <p className={`text-xs mt-1 ${overdue ? "text-red-300" : urgent ? "text-amber-300" : "text-white/60"}`} data-testid="solde-countdown">
              {overdue ? `En retard de ${Math.abs(days)} jour${Math.abs(days) > 1 ? "s" : ""}` :
               days === 0 ? "Aujourd'hui" :
               `Dans ${days} jour${days > 1 ? "s" : ""}`}
            </p>
          )}
        </div>
      </div>

      {/* Payment info */}
      <div className="border border-[#DCB457]/15 p-5 bg-black/30" data-testid="solde-payment-info">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457] mb-4">
          Payer par virement bancaire
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <PayField label="Bénéficiaire" value={pi.beneficiaire} />
          <PayField label="Banque" value={pi.banque} />
          <PayField label="IBAN" value={pi.iban} monospace testid="solde-iban" />
          <PayField label="BIC" value={pi.bic} monospace testid="solde-bic" />
          <PayField label="Montant" value={fmtEur(solde)} highlight />
          <PayField label="Libellé à indiquer" value={pi.libelle} monospace testid="solde-libelle" />
        </div>
        <p className="text-[11px] text-white/50 mt-4 leading-relaxed">
          <strong className="text-white/70">Astuce :</strong> copiez le libellé exact ci-dessus (icône ⧉) pour que le paiement soit rattaché automatiquement à votre dossier.
        </p>
      </div>

      {/* Pay button (Stripe) */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/espace-client/documents"
          data-testid="echeances-pay-solde-cta"
          className="btn-gold inline-flex items-center gap-2"
        >
          <CreditCard size={14} strokeWidth={1.5} /> Payer le solde en ligne (carte bancaire)
        </Link>
        <span className="text-xs text-white/45">
          Paiement sécurisé Stripe · Frais bancaires inclus
        </span>
      </div>
    </div>
  );
}

function PayField({ label, value, monospace = false, highlight = false, testid }) {
  const canCopy = !!value && value !== "—";
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <p className={`${monospace ? "font-mono text-sm" : "text-sm"} ${highlight ? "text-[#DCB457] font-bold" : "text-white/90"} break-all`}>
          {value || "—"}
        </p>
        {canCopy && (
          <button
            type="button"
            onClick={() => copyToClipboard(value, label)}
            className="text-white/40 hover:text-[#DCB457] shrink-0"
            title={`Copier ${label}`}
            data-testid={testid ? `${testid}-copy` : undefined}
          >
            <Copy size={12} strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ testid, icon: Icon, label, value, hint, color = "gold" }) {
  const colorMap = {
    gold: "text-[#DCB457] border-[#DCB457]/25",
    emerald: "text-emerald-300 border-emerald-400/25",
  };
  return (
    <div className={`card-luxe p-6 ${colorMap[color]}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">{label}</p>
        <Icon size={16} strokeWidth={1.4} className={color === "emerald" ? "text-emerald-300" : "text-[#DCB457]"} />
      </div>
      <p className={`font-serif-luxe text-3xl ${color === "emerald" ? "text-emerald-300" : "gold-text"}`}>
        {value}
      </p>
      {hint && <p className="text-xs text-white/40 mt-2">{hint}</p>}
    </div>
  );
}
